import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckIcon, SparklesIcon, RocketIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePricing } from '../contexts/PricingContext';
import { ContactFormPopup } from './ContactFormPopup';
export function Services() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const {
    t
  } = useLanguage();
  const {
    price
  } = usePricing();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  // Replace price in translated strings
  const replacePrice = (text: string) => {
    return text.replace(/€299|\$350|\$249/g, price);
  };
  return <>
      <section id="services" ref={ref} className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
      backgroundColor: 'var(--bg-primary)'
    }} data-id="element-363">
        
        <div className="relative z-10 mx-auto max-w-7xl" data-id="element-364">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} className="mb-12 sm:mb-16 text-center" data-id="element-365">
            
            {/* H2 - Section heading: Pricing & terms */}
            <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl" style={{
            color: 'var(--text-primary)'
          }} data-id="element-366">
              
              {t('pricing.title')} {t('pricing.titleHighlight')}
            </h2>
            <p className="mx-auto max-w-2xl text-base sm:text-lg" style={{
            color: 'var(--text-secondary)'
          }} data-id="element-367">
              
              {t('pricing.subtitle')}
            </p>
          </motion.div>

          {/* Pricing Cards */}
          <div className="grid gap-6 lg:grid-cols-2 max-w-5xl mx-auto" data-id="element-368">
            {/* Free Demo Preview */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.1
          }} data-id="element-369">
              
              <div className="h-full rounded-3xl border p-8 flex flex-col" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)'
            }} data-id="element-370">
                
                <div className="flex items-center gap-3 mb-6" data-id="element-371">
                  <div className="p-3 rounded-xl" style={{
                  backgroundColor: 'var(--bg-tertiary)'
                }} data-id="element-372">
                    
                    <SparklesIcon className="w-6 h-6" style={{
                    color: 'var(--accent)'
                  }} data-id="element-373" />
                    
                  </div>
                  {/* H3 - Free website preview */}
                  <h3 className="text-xl font-bold" style={{
                  color: 'var(--text-primary)'
                }} data-id="element-374">
                    
                    {t('pricing.demo.title')}
                  </h3>
                </div>

                <div className="mb-6" data-id="element-375">
                  <div className="inline-block px-4 py-1 rounded-full text-sm font-bold mb-2" style={{
                  backgroundColor: 'var(--accent)',
                  color: '#fff'
                }} data-id="element-376">
                    
                    {t('pricing.demo.label')}
                  </div>
                  <p className="text-sm sm:text-base font-medium mt-2" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-377">
                    
                    {t('pricing.demo.desc')}
                  </p>
                </div>

                <ul className="space-y-4 mb-8 flex-1" data-id="element-378">
                  {[1, 2, 3].map(i => <li key={i} className="flex items-start gap-3" data-id="element-379">
                      <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center" data-id="element-380">
                        <CheckIcon className="w-3 h-3 text-green-500" data-id="element-381" />
                      </div>
                      <span style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-382">
                      
                        {replacePrice(t(`pricing.demo.b${i}`))}
                      </span>
                    </li>)}
                </ul>

                <button onClick={() => setIsPopupOpen(true)} className="block w-full rounded-xl py-4 text-center text-sm sm:text-base font-semibold transition-all hover:scale-[1.02]" style={{
                backgroundColor: 'var(--accent)',
                color: '#FFFFFF'
              }} data-id="element-383">
                  
                  {t('pricing.demo.cta')}
                </button>
              </div>
            </motion.div>

            {/* Website Launch */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-384">
              
              <div className="h-full rounded-3xl border p-8 flex flex-col relative overflow-hidden" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)'
            }} data-id="element-385">
                
                <div className="flex items-center gap-3 mb-6 relative z-10" data-id="element-386">
                  <div className="p-3 rounded-xl" style={{
                  backgroundColor: 'var(--bg-tertiary)'
                }} data-id="element-387">
                    
                    <RocketIcon className="w-6 h-6" style={{
                    color: 'var(--accent)'
                  }} data-id="element-388" />
                    
                  </div>
                  {/* H3 - Website development (€299 one-time) */}
                  <h3 className="text-xl font-bold" style={{
                  color: 'var(--text-primary)'
                }} data-id="element-389">
                    
                    {t('pricing.launch.title')}
                  </h3>
                </div>

                <div className="mb-6 relative z-10" data-id="element-390">
                  <div className="flex items-baseline gap-2" data-id="element-391">
                    <span className="text-4xl font-bold" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-392">
                      
                      {price}
                    </span>
                    <span className="text-sm" style={{
                    color: 'var(--text-muted)'
                  }} data-id="element-393">
                      
                      {t('pricing.launch.period')}
                    </span>
                  </div>
                  <p className="text-sm sm:text-base font-medium mt-2" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-394">
                    
                    {t('pricing.launch.desc')}
                  </p>
                </div>

                <ul className="space-y-4 mb-8 flex-1 relative z-10" data-id="element-395">
                  {[1, 2, 3, 4].map(i => <li key={i} className="flex items-start gap-3" data-id="element-396">
                      <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center" data-id="element-397">
                        <CheckIcon className="w-3 h-3 text-green-500" data-id="element-398" />
                      </div>
                      <span style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-399">
                      
                        {replacePrice(t(`pricing.launch.b${i}`))}
                      </span>
                    </li>)}
                </ul>

                <button onClick={() => setIsPopupOpen(true)} className="block w-full rounded-xl py-4 text-center text-sm sm:text-base font-semibold transition-all hover:scale-[1.02] relative z-10" style={{
                backgroundColor: 'var(--accent)',
                color: '#FFFFFF'
              }} data-id="element-400">
                  
                  {replacePrice(t('pricing.launch.cta'))}
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Form Popup */}
      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-401" />
      
    </>;
}